import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { Loader2, AlertCircle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function SmartCallback() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Processing SMART callback...");
  const [error, setError] = useState(null);

  useEffect(() => {
    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCallback = async () => {
    try {
      const params = new URLSearchParams(window.location.search);

      // Check for OAuth error response first
      const oauthError = params.get("error");
      if (oauthError) {
        const errorDesc = params.get("error_description") || oauthError;
        setError(`EHR Authorization Error: ${errorDesc}`);
        return;
      }

      const code = params.get("code");
      const state = params.get("state");

      if (!code || !state) {
        // Also check hash fragment (some OAuth providers use fragments)
        const hashParams = new URLSearchParams(window.location.hash.replace("#", "?"));
        const hashCode = hashParams.get("code");
        const hashState = hashParams.get("state");

        if (!hashCode && !hashState) {
          setError("Missing authorization code or state parameter. The EHR authorization may have failed or been cancelled.");
          return;
        }
      }

      const finalCode = code || new URLSearchParams(window.location.hash.replace("#", "?")).get("code");
      const finalState = state || new URLSearchParams(window.location.hash.replace("#", "?")).get("state");

      setStatus("Exchanging authorization code for access token...");

      const res = await axios.post(`${API}/smart/callback`, {
        code: finalCode,
        state: finalState
      });

      if (res.data.session_id) {
        setStatus("Connected! Redirecting...");
        sessionStorage.setItem("smart_session", JSON.stringify(res.data));
        setTimeout(() => navigate("/ehr-connect"), 1000);
      } else {
        setError("Failed to establish SMART session");
      }
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "SMART callback failed";
      setError(detail);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        {error ? (
          <div className="space-y-4">
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-lg font-semibold text-white">Connection Failed</h2>
            <p className="text-red-400 text-sm">{error}</p>
            <p className="text-slate-500 text-xs">
              This may happen if the EHR authorization was cancelled, timed out, or if the sandbox is unavailable.
              Try connecting again from the EHR Connect page.
            </p>
            <Button
              onClick={() => navigate("/ehr-connect")}
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
            >
              Return to EHR Connect
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto" />
            <p className="text-slate-300 font-medium">{status}</p>
            <p className="text-slate-500 text-xs">Connecting to EHR system...</p>
          </div>
        )}
      </div>
    </div>
  );
}
